import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Tooltip, CircularProgress,
  Snackbar, Alert, Link as MuiLink
} from '@mui/material';
import { ArrowLeft, RefreshCw, Send, Trash2, RotateCw } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../Config/config';

const ADMIN_API = `${API_BASE_URL}/api/nextgen-builder/admin`;

const headSx = { fontWeight: '600', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem' };

const StatusChip = ({ status }) => {
  const map = {
    ACCEPTED: { bg: '#dcfce7', fg: '#166534', label: '🟢 Collaborator Active' },
    INVITED: { bg: '#fef9c3', fg: '#854d0e', label: '🟡 Invitation Pending' },
    PENDING: { bg: '#f1f5f9', fg: '#64748b', label: 'Pending' },
    FAILED: { bg: '#fef2f2', fg: '#991b1b', label: '🔴 Invitation Failed' },
    EXPIRED: { bg: '#fef2f2', fg: '#991b1b', label: 'Expired' }
  };
  const c = map[status] || map.PENDING;
  return <Chip label={c.label} size="small" sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700, borderRadius: '6px' }} />;
};

const GitHubAccess = () => {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const fetchInvitations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${ADMIN_API}/github-invitations`);
      if (res.data.success) setInvitations(res.data.data);
    } catch (err) {
      console.error('Failed to fetch GitHub invitations', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to load invitations', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvitations(); }, [fetchInvitations]);

  const runAction = async (inv, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      setBusyId(inv.id);
      const res = await axios.post(`${ADMIN_API}/github-invitations/${inv.id}/${action}`);
      if (res.data.success) {
        setSnackbar({ open: true, message: res.data.message, severity: 'success' });
        fetchInvitations();
      } else {
        setSnackbar({ open: true, message: res.data.message || `${action} failed`, severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || `${action} failed`, severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/nextgen/projects')} sx={{ color: '#843FEF', bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', '&:hover': { bgcolor: '#f0eaff' } }}>
            <ArrowLeft size={24} />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', letterSpacing: '-0.02em' }}>
              GitHub Access
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Collaborator invitations for NextGen Builder repositories
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<RefreshCw size={18} />} onClick={fetchInvitations}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 'bold' }}>
          Refresh
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={8}><CircularProgress sx={{ color: '#843FEF' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ ...headSx, pl: 3 }}>Student</TableCell>
                  <TableCell sx={headSx}>GitHub Username</TableCell>
                  <TableCell sx={headSx}>Repository</TableCell>
                  <TableCell sx={headSx}>Status</TableCell>
                  <TableCell sx={headSx}>Invited</TableCell>
                  <TableCell sx={headSx}>Accepted</TableCell>
                  <TableCell sx={headSx} align="center">Retries</TableCell>
                  <TableCell sx={{ ...headSx, pr: 3 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map(inv => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ pl: 3 }}>
                      <Typography fontWeight="600" color="#1e293b">{inv.full_name || 'Unknown'}</Typography>
                      <Typography variant="caption" color="#64748b">{inv.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <MuiLink href={`https://github.com/${inv.github_username}`} target="_blank" underline="hover" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        @{inv.github_username}
                      </MuiLink>
                    </TableCell>
                    <TableCell>
                      {inv.repository_owner ? (
                        <MuiLink href={`https://github.com/${inv.repository_owner}/${inv.repository_name}`} target="_blank" underline="hover" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#475569' }}>
                          {inv.repository_owner}/{inv.repository_name}
                        </MuiLink>
                      ) : <Typography variant="caption" color="#94a3b8">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={inv.status} />
                      {inv.failed_reason && (
                        <Tooltip title={inv.failed_reason}>
                          <Typography variant="caption" sx={{ display: 'block', color: '#991b1b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {inv.failed_reason}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="caption" color="#64748b">{inv.requested_at ? new Date(inv.requested_at).toLocaleString() : '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="#64748b">{inv.accepted_at ? new Date(inv.accepted_at).toLocaleString() : '—'}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="caption" fontWeight="700" color="#64748b">{inv.retry_count || 0}</Typography></TableCell>
                    <TableCell align="right" sx={{ pr: 3 }}>
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <Tooltip title="Sync status with GitHub">
                          <span><IconButton size="small" disabled={busyId === inv.id} onClick={() => runAction(inv, 'sync')} sx={{ color: '#0284c7' }}>
                            <RotateCw size={16} />
                          </IconButton></span>
                        </Tooltip>
                        {inv.status !== 'ACCEPTED' && (
                          <Tooltip title="Retry / resend invitation">
                            <span><IconButton size="small" disabled={busyId === inv.id} onClick={() => runAction(inv, 'resend')} sx={{ color: '#16a34a' }}>
                              <Send size={16} />
                            </IconButton></span>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove collaborator / cancel invitation">
                          <span><IconButton size="small" color="error" disabled={busyId === inv.id}
                            onClick={() => runAction(inv, 'remove', `Remove @${inv.github_username} from ${inv.repository_owner}/${inv.repository_name}?`)}>
                            <Trash2 size={16} />
                          </IconButton></span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {invitations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography color="#94a3b8" fontWeight="600">No GitHub invitations yet</Typography>
                      <Typography variant="caption" color="#cbd5e1">Invitations are created automatically when students enroll</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GitHubAccess;
